import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { keepPreviousData } from '@tanstack/react-query';

import { CollectionBar } from '@src/pages/Collection/components/CollectionBar';
import { MediaQuery, MediaView, CollectionView } from 'common';
import { Browsing, Pathname, QsNames } from '@src/types/Browsing';
import { useLocationQS } from '@src/utils/useLocationQS';
import { useLocation } from 'react-router-dom';
import { useQueryCollection } from '@src/queries/collections/useQueryCollection';
import {
  useQueryCollectionMediaFacet,
  useQueryCollectionMedias,
} from '@src/queries/collections/useQueryCollectionMedias';
import { useMutationCollectionRemoveMedia } from '@src/queries/collections/useMutationCollection';
import { RightsRequestStatusValue } from '@src/components/RightsRequestStatusSelect';
import { ServiceName, Services } from '@src/types/Services';
import { IPopupsService } from '@src/types/Popups';
import { MediaActionButtonController, MediaActionProps } from '@src/fragments/MediaActionButton/MediaActionController';
import { UncollectButton } from '@src/components/CollectingButtons/Uncollect';
import { GetRightsButton } from '@src/components/IPRights/GetRightsButton';
import { Hide } from '@src/components/Hide';
import { Footer } from '@src/components/Footer';
import { ADALONG_COLORS } from '@src/styles';
import { style, stylesheet } from 'typestyle';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import { MediaLibrary } from '@src/fragments/MediaLibrary';
import { useQueryUserConfig } from '@src/queries/users/useQueryUserConfig';
import { useAddAlert } from '@src/fragments/Alerting/alertAtom';
import { goToGetRights } from '../Media/utils/mediaRoute';
import logo from '../../assets/img/site/logo_alone_white.png';
import { getCollectionLangError } from './collectionErrors';
import NavBar from '../../components/NavBar';
import { CollectionHeader } from './View/CollectionHeader';
import { FilterKey, FiltersView } from '@src/fragments/Filters/FiltersView';
import { useFiltersParams } from '@src/hooks/useFiltersParams';
import { useMediaConstraints } from '@src/hooks/useMediaConstraints';
import { RightRequestMQLStatus } from '@adalong/schemas';
import { XclusionUtility } from '@src/fragments/Filters/managers/XclusionUtility';
import { FacetState } from '@src/pages/Discover/components/Atoms/Atoms';
import { TYPING_DEBOUNCE } from '@src/pages/Discover/DiscoverController';
import { useSetAtom } from 'jotai';
import { MediaFilterBuilder } from '@src/fragments/Filters/utils/MediaFilterBuilder';
import type { MediaConstraints } from '@src/fragments/Filters/utils/MediaFilterBuilder';
import { pathLocationToString, setQsParam } from '@src/utils/browsing';
import { HideStoriesControl, SearchModeControls } from '@src/fragments/MediaLibraryControls';
import { LinearLoader } from '@src/components/LinearLoader';
import { SearchController } from '@src/fragments/Search/SearchController';
import { MARK_THEME } from '@src/config/mark';

interface Props {
  services: Services;
  collectionId: string;
}

export function Collection({ collectionId, ...props }: Props) {
  const [services] = useState<{ browsing: Browsing; popups: IPopupsService }>(() => ({
    browsing: props.services.get<Browsing>(ServiceName.Browsing),
    popups: props.services.get<IPopupsService>(ServiceName.Popups),
  }));
  const [mediaSelection, setMediaSelection] = useState<MediaView[]>([]);
  const addAlert = useAddAlert();

  const [isCollectionBarVisible, setIsCollectionBarVisible] = useState(true);
  const lastScrollTopRef = useRef(0);
  const SCROLL_THRESHOLD = 30;

  const [rightsStatusFilter, setRightsStatusFilter] = useState<RightsRequestStatusValue>('all');
  const [isFetchingStories, setIsFetchingStories] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const { filters, isEmpty: areFiltersEmpty } = useFiltersParams();
  const { isExactSearch, mediaConstraints } = useMediaConstraints(filters);
  const setFacetsState = useSetAtom(FacetState);
  const [filtersOpened, setFiltersOpened] = useState(true);
  const location = useLocation();

  useEffect(() => {
    if (!areFiltersEmpty) {
      setFiltersOpened(true);
    }
  }, [areFiltersEmpty]);

  const {
    t: [token],
    [QsNames.discoverSearchText]: searchText,
    [QsNames.discoverSearchHashtags]: searchHashtags,
    [QsNames.discoverSearchLabels]: searchLabels,
  } = useLocationQS([
    't',
    QsNames.discoverSearchText,
    QsNames.discoverSearchHashtags,
    QsNames.discoverSearchLabels,
  ]);

  const pathLocation = useMemo(() => ({ pathname: location.pathname, search: location.search }), [location.pathname, location.search]);

  const { data: collection, isLoading: isLoadingCollection } = useQueryCollection(collectionId, false, token);
  const { data: userConfig } = useQueryUserConfig();
  const user = userConfig?.user;
  const blacklistedCreatorsLoaded = mediaConstraints.blacklistedCreators !== undefined;
  const canUseFilters = user !== undefined && blacklistedCreatorsLoaded;

  const collectionFacetMediaConstraints = useMemo(() => ({
    ...mediaConstraints,
    collections: collection ? [collection] : [],
  }), [collection, mediaConstraints]);

  const collectionMediaQuery = useMemo<MediaQuery>(
    () => canUseFilters
      ? buildCollectionMediaQuery(
        MediaFilterBuilder.buildMediaFilter(mediaConstraints),
        rightsStatusFilter,
        isExactSearch ? undefined : isFetchingStories
      )
      : { filter: { and: [] } },
    [canUseFilters, mediaConstraints, isExactSearch, isFetchingStories, rightsStatusFilter]
  );

  const partialCountQuery = useMemo<MediaQuery>(
    () => buildCollectionMediaQuery(
      MediaFilterBuilder.buildMediaFilter({
        ...mediaConstraints,
        isExactSearch: false,
      }),
      rightsStatusFilter,
      isFetchingStories
    ),
    [mediaConstraints, isFetchingStories, rightsStatusFilter]
  );

  const exactCountQuery = useMemo<MediaQuery>(
    () => buildCollectionMediaQuery(
      MediaFilterBuilder.buildMediaFilter({
        ...mediaConstraints,
        isExactSearch: true,
      }),
      rightsStatusFilter,
      undefined
    ),
    [mediaConstraints, rightsStatusFilter]
  );

  const {
    data: collectionMediaResponses,
    fetchNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
  } = useQueryCollectionMedias(collectionId, {
    token,
    query: collectionMediaQuery,
  }, {
    enabled: (user !== undefined || !!token) && (user === undefined || blacklistedCreatorsLoaded),
    placeholderData: keepPreviousData,
  });
  const {
    data: partialCountResponses,
    isFetching: isFetchingPartialCount,
  } = useQueryCollectionMedias(collectionId, {
    token,
    query: {
      ...partialCountQuery,
      limit: 0,
    },
  }, {
    enabled: canUseFilters,
    placeholderData: keepPreviousData,
  });
  const {
    data: exactCountResponses,
    isFetching: isFetchingExactCount,
  } = useQueryCollectionMedias(collectionId, {
    token,
    query: {
      ...exactCountQuery,
      limit: 0,
    },
  }, {
    enabled: canUseFilters,
    placeholderData: keepPreviousData,
  });
  useEffect(() => {
    setFacetsState({
      typingDebounce: TYPING_DEBOUNCE,
      enableFacet: canUseFilters && filtersOpened && !!collection && !isLoading,
      mediaConstraints: collectionFacetMediaConstraints,
    });
  }, [canUseFilters, collection, collectionFacetMediaConstraints, filtersOpened, isLoading, setFacetsState]);

  const {
    data: collectionSearchFacetResponses,
    isLoading: isLoadingSearchFacets,
  } = useQueryCollectionMediaFacet(collectionId, {
    token,
    query: collectionMediaQuery,
    input: XclusionUtility.getRawKey(searchInput),
    facets: ['hashtags', 'labels'],
    debounce: 350,
  }, {
    enabled: canUseFilters && filtersOpened,
    placeholderData: keepPreviousData,
  });
  const { mutateAsync: removeMedia, reset: resetRemove } = useMutationCollectionRemoveMedia();

  const medias = collectionMediaResponses?.pages.flatMap(({ medias }) => medias) || [];
  const total = (collectionMediaResponses?.pages || [])[0]?.total || 0;
  const partialTotal = (partialCountResponses?.pages || [])[0]?.total || 0;
  const exactTotal = (exactCountResponses?.pages || [])[0]?.total || 0;
  const searchFacetResult = collectionSearchFacetResponses?.pages[0] as {
    total: number;
    facets: Record<string, string[]>;
  } | undefined;

  const loadMore = async (): Promise<void> => {
    await fetchNextPage();
  };

  // Handle scroll events to show/hide CollectionBar
  const handleScroll = useCallback((scrollTop: number) => {
    const lastScrollTop = lastScrollTopRef.current;

    if (scrollTop === 0) {
      setIsCollectionBarVisible(true);
    } else if (scrollTop > SCROLL_THRESHOLD) {
      if (scrollTop < lastScrollTop) {
        setIsCollectionBarVisible(true);
      } else if (scrollTop > lastScrollTop) {
        setIsCollectionBarVisible(false);
      }
    }

    lastScrollTopRef.current = scrollTop;
  }, []);

  const mediasWithCollection: (MediaView & {
    collection?: CollectionView;
  })[] = medias.map((media) => ({
    ...media,
    collection,
  }));

  const canEdit = collection ? (collection.i_can_edit || collection.mine) : null

  const disableRemoval: MediaActionProps['disable'] = { removeFromCollection: lang.disableRemove };

  // loader for the first page only
  const isLibraryLoading: boolean = medias.length === 0 && isLoading;
  const displayFetchingLoader = medias.length > 0 && isFetching && !isFetchingNextPage;

  if ((user === undefined && token === undefined) || token === '') {
    return null;
  }

  return (
    <div id='collections' className={collectionsClass}>
      {user !== undefined ? (
        <div className={navBarContainerClass}>
          <NavBar active={Pathname.collect} />
        </div>
      ) : (
        <nav id='navbar' className='navbar navbar-toggleable-md'>
          <div className='logo'>
            <img src={logo as string} className={logoImgClass} />
          </div>
        </nav>
      )}
      <div className={sheet.collection}>
        {user !== undefined ? (
          <div
            className={`${collectionBarContainerClass} ${isCollectionBarVisible ? collectionBarVisibleClass : collectionBarHiddenClass}`}
          >
            <CollectionBar />
          </div>
        ) : null}
        <CollectionHeader
          filtersOpened={filtersOpened}
          onToggleFilters={() => setFiltersOpened(v => !v)}
          rightsStatusFilter={rightsStatusFilter}
          setRightsStatusFilters={setRightsStatusFilter}
          isLoading={isLoadingCollection}
          collection={collection || null}
          totalResults={total}
          mediaSelection={mediaSelection}
          setMediaSelection={setMediaSelection}
          services={services}
        />
        <div className={sheet.contentBody}>
          {canUseFilters ? (
            <div className={sheet.filtersContainer} data-hide={String(!filtersOpened)}>
              <FiltersView
                enableProductFilter={user?.groupFeatures.product_catalog || false}
                enableAudioFilter={user?.groupFeatures.audio_filters || false}
                catalogCountriesFilterValues={user?.catalogCountries || []}
                creatorCountriesFilterValues={user?.catalogCountries || []}
                hideFilters={[FilterKey.collection]}
                topSlot={
                  <SearchController
                    services={services}
                    pathLocation={pathLocation}
                    qsValues={{
                      text: searchText,
                      hashtags: searchHashtags,
                      labels: searchLabels,
                    }}
                    state={{
                      input: searchInput,
                      setInput: setSearchInput,
                      isLoading: isLoadingSearchFacets,
                      result: searchFacetResult
                        ? {
                          media: [],
                          total: searchFacetResult.total,
                          facets: searchFacetResult.facets,
                        }
                        : undefined,
                    }}
                    theme={MARK_THEME}
                    compact
                    suggestionsCompact={false}
                    suggestionsWide
                  />
                }
              />
            </div>
          ) : null}
          <div className={sheet.libraryContainer}>
            {canUseFilters ? (
              <div className={sheet.libraryControls}>
                <div className={sheet.libraryControlsLeft}>
                  {user?.groupFeatures.crm ? (
                    <HideStoriesControl
                      isFetchingStories={isFetchingStories}
                      setIsFetchingStories={setIsFetchingStories}
                    />
                  ) : null}
                </div>
                <SearchModeControls
                  isExactMode={isExactSearch}
                  estimatedCount={partialTotal}
                  exactCount={exactTotal}
                  isFetching={isFetchingPartialCount || isFetchingExactCount}
                  onSearchModeChange={setExactMode}
                />
              </div>
            ) : null}
            <LinearLoader
              display={displayFetchingLoader}
              height={2}
              classes={[sheet.libraryFetchingLoader]}
            />
            <MediaLibrary
              hideOnImageFailed={true}
              loadMoreMedia={loadMore}
              setSelection={setMediaSelection}
              selection={mediaSelection}
              libraryLoaded={!isLibraryLoading}
              mediaList={mediasWithCollection}
              noMore={!hasNextPage}
              onScroll={handleScroll}
              renderMediaActionButton={(media) => (
                <MediaActionButtonController
                  services={services}
                  inCollection={collection}
                  // If the collection can't be edited we disable the remove
                  // from collection button and display a tooltip
                  disable={!canEdit ? disableRemoval : undefined}
                  mediaList={[media]}
                  catalogCountries={user?.catalogCountries}
                />
              )}
              renderShortcutActionButton={(media, failed) =>
                // Get rights is the main button here but we show the
                // removal button in case of an unavailable media
                failed ? (
                  <UncollectButton onClick={() => handleRemoveMedia(media._id)} />
                ) : (
                  <Hide hide={media.rightsRequestStatus === RightRequestMQLStatus.Agreed}>
                    <GetRightsButton onAction={() => services.browsing.goToPage(goToGetRights(window.location, media._id))} size='small' />
                  </Hide>
                )
              }
            />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );

  function handleRemoveMedia(mediaId: string) {
    if (collection !== undefined) {
      removeMedia(
        {
          mediaIds: [mediaId],
          collectionId: collection?._id,
        },
        {
          onError: (error) =>
            addAlert({
              text: getCollectionLangError(error),
              close: resetRemove,
            }),
        }
      ).catch(console.error);
    }
  }

  function setExactMode(mode: boolean) {
    services.browsing.goToPage(
      pathLocationToString(
        setQsParam(location, [
          {
            field: QsNames.discoverExactMode,
            val: mode ? 'true' : undefined,
          },
        ])
      )
    );
  }
}

function buildCollectionMediaQuery(
  filterBuilt: Pick<MediaQuery, 'filter' | 'search' | 'rawOnly' | 'withStories'>,
  rightsStatusFilter: RightsRequestStatusValue,
  withStories?: MediaConstraints['withStories']
): MediaQuery {
  const and = [
    ...(filterBuilt.filter.and || []),
  ];

  if (rightsStatusFilter !== 'all') {
    and.push({ rightRequestStatuses: [rightsStatusFilter] });
  }

  return {
    ...filterBuilt,
    withStories,
    filter: {
      ...filterBuilt.filter,
      and,
    },
  };
}

const collectionsClass = style({
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: ADALONG_COLORS.SMOOTH_BLACK,
});

const navBarContainerClass = style({
  backgroundColor: ADALONG_COLORS.DARK_BLACK,
});


const logoImgClass = style({
  cursor: 'default',
});

const collectionBarContainerClass = style({
  transition: 'max-height 0.3s ease, opacity 0.3s ease',
  zIndex: 2,
  overflow: 'hidden',
  paddingTop: 5,
});

const collectionBarVisibleClass = style({
  maxHeight: '300px',
  opacity: 1,
  marginBottom: '0px',
});

const collectionBarHiddenClass = style({
  maxHeight: '0px',
  opacity: 0,
  marginBottom: '0px',
});

const sheet = stylesheet({
  collection: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  contentBody: {
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
    position: 'relative',
    height: '100%',
    flex: 1,
  },
  filtersContainer: {
    flexShrink: 0,
    width: 320,
    height: '100%',
    position: 'relative',
    overflow: 'visible',
    $nest: {
      '&[data-hide="true"]': {
        width: 0,
      },
      '&>*': {
        height: '100%',
      },
    },
  },
  libraryContainer: {
    flex: 1,
    padding: '0 3px 0 12px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 1,
  },
  libraryControls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 15px 8px 5px',
    minHeight: 28,
  },
  libraryControlsLeft: {
    display: 'flex',
    alignItems: 'center',
  },
  libraryFetchingLoader: {
    margin: '0 15px 6px 5px',
  },
  filterTitle: {
    flexShrink: 0,
    width: 320,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    padding: '10px 15px 7px 5px',
  },
});

const lang = LocalizedStrings({
  en: {
    disableRemove: "You don't have the permission to remove content from this collection",
  },
});
